/**
 * Whether the kitchen display currently has a live socket.
 *
 * The order queries read this to decide how hard to poll: while the socket is
 * up the server pushes every change that matters, so a poll is only a safety
 * net; while it is down the poll is the only way a comanda reaches the cook,
 * and it has to be quick.
 *
 * It lives here, apart from the socket itself, for two reasons: the decision is
 * testable without a browser, and a display mounts one socket but several
 * queries in different components, so threading a boolean through all of them
 * would buy nothing.
 */

let openCount = 0;
const listeners = new Set<() => void>();

/** True while at least one socket is open. */
export function isKitchenWsConnected(): boolean {
  return openCount > 0;
}

/** Called whenever the answer changes. Returns the unsubscribe. */
export function subscribeKitchenWsStatus(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export interface WsPresence {
  /** Report this socket as open or closed. Repeating a state does nothing. */
  set(open: boolean): void;
}

/**
 * One socket's share of the count.
 *
 * Each socket gets its own handle and can only ever contribute one. A socket
 * that reports closed twice — a close event followed by an explicit teardown,
 * which is the ordinary sequence on unmount — must not drive the count
 * negative, or the display would go on believing it has a socket it lost and
 * poll slowly with nothing pushing to it.
 */
export function kitchenWsPresence(): WsPresence {
  let counted = false;
  return {
    set(open: boolean): void {
      if (open === counted) return;
      counted = open;
      openCount += open ? 1 : -1;
      for (const listener of listeners) listener();
    },
  };
}

/** Test seam: forget every socket and listener. */
export function resetKitchenWsStatus(): void {
  openCount = 0;
  listeners.clear();
}
